import React from "react";
import { motion } from "framer-motion";
import { Hammer, MessageSquare, Quote, Shield, Zap, TrendingUp } from "lucide-react";

const ExtraSections = () => {
  const testimonials = [
    {
      quote: "Chahat has a rare ability to bridge the gap between AI automation and complex Salesforce CRM systems. He built synchronization tools that completely modernized our lead pipelines, saving us dozens of hours each week.",
      name: "Senior Architect",
      company: "TwoPir Consulting Client",
      avatar: "https://images.unsplash.com/photo-1534528741775-53994a69daeb?q=80&w=256&auto=format&fit=crop"
    },
    {
      quote: "StockPilot was delivered with integrated Genkit recommendation engines ahead of schedule. Chahat owns fullstack tasks end-to-end and makes technical choices that solve real business problems.",
      name: "Engineering Manager",
      company: "Enterprise SaaS Client",
      avatar: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?q=80&w=256&auto=format&fit=crop"
    }
  ];

  const currentlyBuilding = [
    {
      title: "SafariCraft Immersive Storefront & AI Tools",
      tech: "Shopify Liquid, Preact, NodeJS, GraphQL Admin API",
      progress: 80,
      description: "Safari-themed premium Shopify storefront featuring shoppable product hotspots, visual comparison sliders, dynamic bento grids, and Preact app extensions."
    },
    {
      title: "AI Sales & Voice Agent Platform",
      tech: "NextJS, Fastify, Twilio Voice, OpenAI Whisper",
      progress: 75,
      description: "An autonomous agent that dials outbound leads, qualifies them using OpenAI assistant tools, and syncs status instantly with CRM databases."
    },
    {
      title: "Realtime Multi-Tenant CRM Hub",
      tech: "React, Node, PostgreSQL, Redis, Socket.io",
      progress: 40,
      description: "Enterprise dashboard with health calculations, compound filter arrays, and bidirectional sync pipelines."
    }
  ];

  return (
    <div className="py-16 px-6 max-w-7xl mx-auto text-white space-y-20">
      
      {/* Testimonials & Currently Building Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
        
        {/* Testimonials Card Grid */}
        <div className="space-y-6">
          <div className="flex items-center gap-2.5 mb-4">
            <span className="p-2 bg-pink-500/20 rounded-xl border border-pink-400/30">
              <MessageSquare className="w-5 h-5 text-pink-400" />
            </span>
            <h3 className="text-2xl font-bold">Client Testimonials</h3>
          </div>

          <div className="space-y-6">
            {testimonials.map((t, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: idx * 0.1 }}
                className="bg-white/5 border border-white/10 p-6 rounded-2xl relative shadow-xl backdrop-blur-md hover:border-pink-500/30 transition-all duration-300"
              >
                <Quote className="absolute top-4 right-4 w-8 h-8 text-white/5" />
                <p className="text-gray-300 text-xs md:text-sm leading-relaxed mb-4 italic">
                  "{t.quote}"
                </p>
                <div className="flex items-center gap-3">
                  <img
                    src={t.avatar}
                    alt={t.name}
                    className="w-10 h-10 rounded-full object-cover border border-pink-400/30"
                  />
                  <div>
                    <h4 className="text-xs font-bold text-white">{t.name}</h4>
                    <span className="text-[10px] text-pink-400 font-semibold">{t.company}</span>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Currently Building */}
        <div className="space-y-6">
          <div className="flex items-center gap-2.5 mb-4">
            <span className="p-2 bg-yellow-500/20 rounded-xl border border-yellow-400/30">
              <Hammer className="w-5 h-5 text-yellow-400 animate-pulse" />
            </span>
            <h3 className="text-2xl font-bold">Currently Building</h3>
          </div>

          <div className="space-y-6">
            {currentlyBuilding.map((p, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: idx * 0.1 }}
                className="bg-white/5 border border-white/10 p-6 rounded-2xl shadow-xl backdrop-blur-md hover:border-yellow-500/30 transition-all duration-300"
              >
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-sm font-bold text-white tracking-wide">{p.title}</h4>
                  <span className="text-[10px] bg-yellow-500/10 border border-yellow-500/30 text-yellow-400 font-semibold px-2 py-0.5 rounded-full">
                    Active Dev
                  </span>
                </div>
                <p className="text-xs text-gray-400 mb-4 leading-relaxed">{p.description}</p>
                
                {/* Progress bar */}
                <div>
                  <div className="flex justify-between text-[9px] text-gray-500 font-bold mb-1">
                    <span>Stack: {p.tech}</span>
                    <span>{p.progress}% Completed</span>
                  </div>
                  <div className="w-full bg-white/5 rounded-full h-1.5 overflow-hidden">
                    <div 
                      className="bg-gradient-to-r from-yellow-400 to-amber-500 h-full rounded-full transition-all duration-500" 
                      style={{ width: `${p.progress}%` }}
                    />
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

      </div>

      {/* Client Consulting & Solutions Delivery Section */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="bg-gradient-to-br from-[#0a1224]/50 to-[#0e172e]/50 border border-white/10 p-8 rounded-3xl shadow-2xl relative overflow-hidden"
      >
        <div className="absolute top-0 right-0 p-8 opacity-5">
          <Shield className="w-32 h-32 text-white" />
        </div>

        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
          <div>
            <div className="flex items-center gap-2.5 mb-2">
              <span className="p-2 bg-cyan-500/20 rounded-xl border border-cyan-400/30">
                <Shield className="w-5 h-5 text-cyan-400" />
              </span>
              <h3 className="text-2xl font-bold">Client Consulting & Solutions Delivery</h3>
            </div>
            <p className="text-gray-400 text-xs md:text-sm max-w-xl leading-relaxed">
              Experienced in translating complex business processes into highly available, automated software products. Collaborating directly with founders, CRM heads, and product teams to ship secure API webhooks, sync pipelines, and databases under strict timelines.
            </p>
          </div>

          <div className="flex flex-wrap gap-4 md:self-center">
            <div className="bg-white/5 border border-white/10 px-5 py-4 rounded-2xl text-center min-w-[110px]">
              <TrendingUp className="w-5 h-5 text-cyan-400 mx-auto mb-1.5" />
              <div className="text-xl font-extrabold text-white">20+</div>
              <div className="text-[10px] text-gray-500 uppercase font-bold mt-0.5">Delivered Clients</div>
            </div>
            <div className="bg-white/5 border border-white/10 px-5 py-4 rounded-2xl text-center min-w-[110px]">
              <Zap className="w-5 h-5 text-emerald-400 mx-auto mb-1.5 animate-pulse" />
              <div className="text-xl font-extrabold text-white">99.9%</div>
              <div className="text-[10px] text-gray-500 uppercase font-bold mt-0.5">Integration SLA</div>
            </div>
            <div className="bg-white/5 border border-white/10 px-5 py-4 rounded-2xl text-center min-w-[110px]">
              <Shield className="w-5 h-5 text-indigo-400 mx-auto mb-1.5" />
              <div className="text-xl font-extrabold text-white">100%</div>
              <div className="text-[10px] text-gray-500 uppercase font-bold mt-0.5">Secure Code</div>
            </div>
          </div>
        </div>
      </motion.div>

    </div>
  );
};

export default ExtraSections;
